// Angular
import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
// RxJS
import { AuthService } from '@core/security/services/auth.service';
import { Store } from '@ngrx/store';
import { ValidateUtil } from '@utils/classes/ValidateUtil';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ApiResponse } from '../../../@utils/models/ApiResponse';
import { CookiesService } from '../../../@utils/services/cookies.service';
import { MessageUtilService } from '../../../@utils/services/message-util.service';
import { Logout } from '../../store/actions/auth.actions';
import { AppState } from '../../store/index';

@Injectable({
	providedIn: 'root',
})
export class ErrorHandlerInterceptorService implements HttpInterceptor {
	refreshTokenInProgress = false;
	tokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private readonly _authService: AuthService,
		private readonly _cookiesService: CookiesService,
		private readonly _messageUtilService: MessageUtilService,
		private _store: Store<AppState>
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			catchError((e: HttpErrorResponse) => {
				const apiError = this._asApiResponse(e.error);
				if ((e instanceof HttpErrorResponse && e.status === 403) || (e.status === 401 && this._isTokenExpiredCode(apiError?.codigo))) {
					if (!this.refreshTokenInProgress) {
						this.refreshTokenInProgress = true;
						this.tokenSubject.next(false);
						return this._authService.refreshToken().pipe(
							switchMap((authResponse) => {
								this.refreshTokenInProgress = false;
								this._authService.updateTokens(authResponse.idToken!, authResponse.accessToken!, authResponse.maestroSyncToken!);
								this.tokenSubject.next(true);
								return next.handle(this.getCloneRequest(req));
							}),
							catchError((e: HttpErrorResponse) => {
								this.refreshTokenInProgress = false;
								return throwError(e);
							})
						);
					} else {
						return this.tokenSubject.pipe(
							filter((idToken) => idToken !== false),
							take(1),
							switchMap((idToken) => {
								return next.handle(this.getCloneRequest(req));
							})
						);
					}
				}
				return this.throwError(e);
			})
		);
	}

	getCloneRequest(req: HttpRequest<any>) {
		let cloneRequest;
		if (req.url.indexOf('sumax-erp-backend-maestros-sync') !== -1) {
			cloneRequest = req.clone({
				headers: req.headers.set('Authorization', this._cookiesService.getBearerAuthorization('maestroSyncToken')),
			});
		} else if (req.url.indexOf('sumax-dms') != -1) {
			let headers = new HttpHeaders({ nroDocumento: '20414485554', usuario: 'TRACKING', clave: 'tracking212598' });
			return req.clone({ headers });
		} else {
			cloneRequest = req.clone({
				headers: req.headers.set('Authorization', this._cookiesService.getBearerAuthorization('idToken')),
			});
		}
		return cloneRequest;
	}

	private throwError(err: HttpErrorResponse) {
		if (!ValidateUtil.isUndefinedOrNull(err.status)) {
			const apiError = this._asApiResponse(err.error);
			const mensaje = (apiError?.mensaje ?? this._getMensajeUsuario(err))?.toString() ?? this._getMensajeUsuario(err);

			const type = apiError?.type?.toString();
			if (type && this._isWarningType(type)) {
				this._messageUtilService.getAlertWarning(mensaje);
			} else {
				this._messageUtilService.getAlertError('A999', mensaje);
			}
		}
		return throwError(err);
	}

	private _asApiResponse(value: unknown): ApiResponse | null {
		if (!value || typeof value !== 'object') return null;
		const v = value as Record<string, unknown>;
		if (!('codigo' in v) || !('mensaje' in v)) return null;
		return value as ApiResponse;
	}

	private _isTokenExpiredCode(codigo: unknown): boolean {
		if (codigo === null || codigo === undefined) return false;
		const raw = String(codigo).trim();
		return raw === '000046' || raw === 'GEN000046';
	}

	private _isWarningType(type: string): boolean {
		const t = type.trim().toUpperCase();
		return t === 'A';
	}

	private _getMensajeUsuario(err: HttpErrorResponse): string {
		switch (err.status) {
			case 0:
				return 'No se pudo conectar con el servidor';
			case 401:
				return 'Tu sesión ha expirado';
			case 403:
				return 'No tienes permisos para esta acción';
			case 404:
				return 'Recurso no encontrado';
			case 500:
				return 'Ocurrió un error interno';
			case 502:
				return 'Ocurrió un problema de comunicación con el servidor. Intenta nuevamente más tarde.';
			case 503:
				return 'Servicio temporalmente no disponible';
			case 504:
				return 'No pudimos completar la operación porque el servidor tardó demasiado en responder. Intenta nuevamente más tarde.';
			default:
				return 'Ocurrió un error inesperado';
		}
	}
}

export const errorHandlerInterceptorProviders = {
	provide: HTTP_INTERCEPTORS,
	useClass: ErrorHandlerInterceptorService,
	multi: true,
};
